<script setup lang="ts">
import Swiper from 'swiper'
import 'swiper/css'
import type {
  CatalogsNewItem,
  CatalogsNewResponse,
} from '#shared/interfaces/top/catalogsNew'
import { TRANSITION_SOURCE } from '#shared/constant/constant'

/**
 * 最新自動車カタログ
 */
const { data: catalogsNewResponse } = await useFetch<CatalogsNewResponse>(
  '/api/catalogs/new',
  {
    key: 'catalogs-new',
    cache: 'no-cache',
  }
)
const catalogsNewData = computed<Array<CatalogsNewItem>>(() => {
  const newest = catalogsNewResponse.value?.data?.newest
  return newest instanceof Array ? [...newest] : []
})

const clickTag = useState('latest_catalog_click', () => false)
const showLatestFlg = ref(false)
const initSwiper = () => {
  nextTick(() => {
    new Swiper('.swiper-latest', {
      modules: [],
      slidesPerView: 2.5, // current view slide
      spaceBetween: 12, // space slide Between slide
      // Responsive breakpoints
      breakpoints: {
        // when window width is >= 320px
        320: {
          slidesPerView: 1.8,
          spaceBetween: 12,
        },
        375: {
          slidesPerView: 2.2,
          spaceBetween: 12,
        },
        // when window width is >= 480px
        480: {
          slidesPerView: 3,
          spaceBetween: 12,
        },
        // when window width is >= 640px
        640: {
          slidesPerView: 4,
          spaceBetween: 12,
        },
        // when window width is >= 640px
        768: {
          slidesPerView: 5,
          spaceBetween: 12,
        },
      },
    })
  })
}

const nuxtApp = useNuxtApp()
const sendEvent = (carCd: number, modelCd: number) => {
  if (!clickTag.value) {
    clickTag.value = true
    nuxtApp.$sendEventGaTag('CatalogNewCarCatalogTap')
    navigateTo({
      path: '/model-top/' + carCd + '_' + modelCd,
      query: { transition_source: TRANSITION_SOURCE.latest_catalog },
    })
  }
}

const fixCarName = (carName: string) => {
  if (carName.length > 8 && carName.length <= 18) {
    return carName.slice(0, 9) + `\n` + carName.slice(9)
  }
  if (carName.length > 18) {
    return carName.substring(0, 18) + '･･･'
  }
  return carName
}
onMounted(() => {
  clickTag.value = false
  showLatestFlg.value = true
  initSwiper()
})
</script>

<template>
  <div v-if="catalogsNewData.length !== 0">
    <p class="fw-bold">最新自動車カタログ</p>
  </div>
  <div class="row slide-latest">
    <div class="swiper-box">
      <div class="swiper-latest swiper-initialized swiper-horizontal">
        <div class="swiper-wrapper">
          <div
            v-for="catalog in catalogsNewData"
            :key="catalog.car_cd"
            class="swiper-slide"
          >
            <div v-if="showLatestFlg" class="slide">
              <ul class="border" style="margin: 0 12px 0 0">
                <li class="noPadding">
                  <div
                    class="underscore-link"
                    @click="sendEvent(catalog.car_cd, catalog.model_cd)"
                  >
                    <div class="card_text_info">
                      <div class="brand_name">
                        {{ catalog.brand_name }}
                      </div>
                      <img
                        :src="catalog.photo_uri"
                        class="card-img-top"
                        :alt="catalog.car_name"
                        onerror="
                          this.src =
                            'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
                        "
                        loading="eager"
                      />
                      <div class="car_name">
                        {{ fixCarName(catalog.car_name) }}
                      </div>
                      <div class="release-date">
                        {{
                          catalog.release_date.replace(
                            /(\d\d\d\d)-(\d\d)-(\d\d)/g,
                            '$2月$3日'
                          )
                        }}
                      </div>
                      <div
                        v-if="catalog.newcar_price_lower_limit"
                        class="text_price"
                      >
                        新車価格<span class="number_price">
                          {{
                            catalog.newcar_price_lower_limit.toLocaleString()
                          }}
                        </span>
                        <span class="text_currency">円〜</span>
                      </div>
                      <div v-else class="text_price">
                        新車価格<span class="number_price">ーー</span>
                      </div>
                      <div class="release-description">
                        {{ catalog.release_description }}
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.slide-latest {
  margin-top: 10px;
  padding-left: 0px;
}
.swiper-box {
  padding: 0 0 0 0;
}

.swiper-latest {
  display: flex;
  overflow: hidden;
  padding: 0 16px 0 16px;
}
.slide {
  padding: 0px;
}
.slide ul {
  margin: 0;
  padding: 0;
  border-radius: 5px;
  background: #fff;
  text-align: left;
  min-height: 153px;
  min-width: 146px;
  box-shadow:
    rgb(212 212 217 / 25%) 0px 1px 2px -1px,
    rgb(175 171 171 / 30%) 2px 1px 4px 0px;
  margin-right: 7px;
}

.slide ul li {
  list-style: none;
  font-size: 12px;
  margin: 5px 0;
  padding: 0 10px;
}

.slide ul li img {
  max-width: 100%;
  display: block;
  padding: 0 8px;
}

.slide ul li.noPadding {
  padding: 0;
  margin-top: 0;
}

.swiper-slide .name {
  font-size: 12px;
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 5px;
}

.card_text_info {
  text-align: center;
}

.brand_name,
.car_name {
  text-align: center;
  color: #333333;
  font-weight: bold;
  white-space: pre-wrap;
}

.brand_name {
  font-size: 10px;
  margin-top: 6px;
  margin-bottom: 4px;
}

.car_name {
  font-size: 12px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  min-height: 38px;
}
.card_text_info .text_price {
  font-size: 10px;
  margin: 0 4px 0 4px;
}

.card_text_info .number_price {
  font-size: 11px;
  font-weight: bold;
  margin-left: 4px;
  overflow-wrap: break-word;
}

.card_text_info .text_currency {
  font-size: 11px;
}

.release-date {
  font-size: 11px;
  min-height: 21px;
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.release-description {
  font-size: 10px;
  border: 1px solid #ecf0f2;
  width: fit-content;
  height: auto;
  border-radius: 2px;
  margin-left: auto;
  margin-right: auto;
  padding: 0px 4px 0px 4px;
  margin-top: 9px;
  margin-bottom: 10px;
}

.underscore-link {
  color: #000000;
}

img.card-img-top {
  height: 66px;
  vertical-align: middle;
  object-fit: cover;
}
</style>
